/**
 * IdP Session Handler Hook - Handles IdP session expiration events.
 *
 * This hook listens for custom events dispatched by WeBuildClientProvider when
 * token refresh fails, and updates the session store to show the warning modal.
 *
 * This is Pathway-specific functionality that bridges the WeBuildClientProvider
 * (which detects IdP session expiration) with the react-auth SessionTimeoutProvider
 * (which manages the warning modal and logout flow).
 *
 * @see docs/architecture/decisions/0010-session-timeout-management.md
 */

import { useEffect, useRef, useState } from "react";
import { useSessionStore } from "@webuild-ai/react-auth/stores";

import { SESSION_EVENTS, type IdpSessionExpiredEventDetail } from "@/types/sessionEvents";
import { logger } from "@/telemetry";

export interface UseIdpSessionHandlerOptions {
  /** Whether the handler is enabled. Set to false during auth loading to avoid race conditions. */
  enabled?: boolean;
}

/**
 * Hook to handle IdP session expiration events.
 *
 * Listens for SESSION_EVENTS.IDP_SESSION_EXPIRED events and updates the session
 * store to show the warning modal with a 30-second countdown.
 *
 * @param options - Configuration options
 * @param options.enabled - Whether to enable the handler (default: true)
 *
 * @example
 * ```tsx
 * function App() {
 *   const { isLoading, isAuthenticated } = useAuth();
 *   useIdpSessionHandler({ enabled: !isLoading && isAuthenticated });
 *   return <YourAppContent />;
 * }
 * ```
 */
export function useIdpSessionHandler({ enabled = true }: UseIdpSessionHandlerOptions = {}): void {
  // Track if we're handling an IdP expiration to prevent duplicates
  // Using ref to avoid re-registering event listener on state changes
  const idpExpirationPendingRef = useRef(false);
  const [idpExpirationPending, setIdpExpirationPending] = useState(false);
  const showWarningModal = useSessionStore((state) => state.showWarningModal);

  // Keep ref in sync with state
  idpExpirationPendingRef.current = idpExpirationPending;

  // Listen for IdP session expiration events from WeBuildClientProvider
  useEffect(() => {
    // Don't set up listener if disabled (e.g., during auth loading)
    if (!enabled) {
      return;
    }

    const handleIdpExpired = (event: Event) => {
      const customEvent = event as CustomEvent<IdpSessionExpiredEventDetail>;

      // Prevent duplicate handling (use ref to avoid stale closure)
      if (idpExpirationPendingRef.current) {
        logger.info("IdP expiration already being handled, ignoring duplicate event");

        return;
      }

      logger.info("IdP session expired event received", {
        reason: String(
          (customEvent.detail as unknown as Record<string, unknown>)?.reason ?? "unknown",
        ),
      });
      setIdpExpirationPending(true);

      // Show warning modal with 30-second countdown for user to acknowledge
      // Using getState() to avoid stale closure issues
      useSessionStore.getState().setShowWarningModal(true, "idp_revoked");
      useSessionStore.getState().setTimeUntilExpiry(30 * 1000);
    };

    window.addEventListener(SESSION_EVENTS.IDP_SESSION_EXPIRED, handleIdpExpired);

    return () => {
      window.removeEventListener(SESSION_EVENTS.IDP_SESSION_EXPIRED, handleIdpExpired);
    };
  }, [enabled]); // Only re-register when enabled changes

  // Reset idpExpirationPending when modal closes (user logged out)
  useEffect(() => {
    if (!showWarningModal && idpExpirationPending) {
      setIdpExpirationPending(false);
    }
  }, [showWarningModal, idpExpirationPending]);
}
